import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Input, Button, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setShowAISearchFeedbackModal } from 'actions/searchNLP';
import { getSearchView } from 'selectors/search';
import {
  getShowAISearchFeedbackModal,
  getPendingSearchView,
  getPendingAiSessionId,
  getActiveAiSessionId,
  getSessionFeedback,
  getAiFeedbackSubject,
  getOrgAiFeedbackSubject,
} from 'selectors/searchNLP';
import { getFilters as getOrgFilters } from 'selectors/organisation';
import {
  ActivitySearch,
  OrganisationSearch,
  ResearcherSearch,
} from 'components/Search/Results/helpers';
import { usePost } from 'hooks';
import { aiSearchFeedback } from 'services/api';
import { colours } from 'utils/theme';
import { useNlpOrgContext } from './NlpOrgContext';
import { REQUEST_SOURCE } from './requestSource';
import * as strings from './strings';

function resolveSearchTypeLabel(viewKey) {
  if (ActivitySearch(viewKey)) {
    return 'Activity';
  }
  if (ResearcherSearch(viewKey)) {
    return 'Researcher';
  }
  if (OrganisationSearch(viewKey)) {
    return 'Organisation';
  }
  return null;
}

/**
 * Feedback payload matching the C# contract (PascalCase). Reads from the feedback session
 * record, not the live aiSearchData, so the payload stays whole even after provenance was
 * cleared. Every field falls back to a safe default so feedback works before any search.
 * FilterResponse must always be a real object — the backend calls GetRawText() on it.
 * Exported for tests.
 */
export function buildFeedbackPayload({
  sessionId,
  rating,
  comment,
  sessionFeedback,
  mainSearchView,
  orgCtx,
}) {
  // On an org profile the main-search view selectors are stale leftovers, so never
  // guess a search type from them there; the session record (when present) still wins.
  const searchType =
    sessionFeedback?.searchType ||
    (orgCtx ? null : resolveSearchTypeLabel(mainSearchView));

  // The route param arrives as a string; the contract wants int?. Anything
  // non-numeric degrades to null rather than failing model binding.
  const organisationId = orgCtx ? Number(orgCtx.organisationId) : NaN;

  return {
    SessionId: sessionId || '',
    Rating: rating,
    Comment: comment.trim() || null,
    NaturalLanguageQuery: { queries: sessionFeedback?.queries ?? [] },
    FilterResponse: {
      filters: sessionFeedback?.filters ?? {},
      events: sessionFeedback?.events ?? [],
    },
    SearchResultsCount: sessionFeedback?.searchResultsCount ?? 0,
    SearchType: searchType,
    RequestSource: orgCtx
      ? REQUEST_SOURCE.organisationProfile
      : REQUEST_SOURCE.mainSearch,
    OrganisationId: Number.isInteger(organisationId) ? organisationId : null,
    OrganisationName: orgCtx?.organisationName ?? null,
  };
}

const { TextArea } = Input;

const BetaBadge = styled.span`
  background-color: ${colours.infoLightBlue};
  color: ${colours.blue};
  padding: 0px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
`;

const RatingContainer = styled.div`
  display: flex;
  gap: 12px;
  margin: 16px 0 0 0;
`;

const RatingButton = styled.button`
  flex: 1;
  padding: 12px 0;
  font-size: 18px;
  font-weight: 500;
  background-color: ${p => (p.selected ? '#1890ff' : 'white')};
  color: ${p => (p.selected ? 'white' : 'black')};
  border: 1px solid ${p => (p.selected ? '#1890ff' : '#e1e1e1')};
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${p => (p.selected ? '#40a9ff' : '#f5f5f5')};
    border-color: ${p => (p.selected ? '#40a9ff' : '#d9d9d9')};
  }
`;

const RatingLabels = styled.div`
  display: flex;
  justify-content: space-between;
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 8px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export function NLPFeedbackModal() {
  const dispatch = useDispatch();
  const showModal = useSelector(getShowAISearchFeedbackModal);
  const view = useSelector(getSearchView);
  const pendingSearchView = useSelector(getPendingSearchView);
  // Non-null only when mounted on an organisation profile (via SciAIOrgSearch):
  // feedback then reports that page as its source, naming the organisation.
  const orgCtx = useNlpOrgContext();
  // On a profile the global session pointers are persisted main-search leftovers; only its own count.
  const orgFilters = useSelector(getOrgFilters(orgCtx?.organisationId));
  const orgSessionId = orgFilters?.aiFilterSessionId ?? null;
  // No AI generation to talk about: ask for general feedback rather than about results.
  const feedbackSubject = useSelector(
    orgCtx ? getOrgAiFeedbackSubject(orgSessionId) : getAiFeedbackSubject
  );
  const freeTextTitle = feedbackSubject
    ? strings.feedbackModalFreeTextTitle
    : strings.feedbackModalGeneralFreeTextTitle;
  const freeTextPlaceholder = feedbackSubject
    ? strings.feedbackModalFreeTextPlaceholder
    : strings.feedbackModalGeneralFreeTextPlaceholder;
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');

  const pendingSessionId = useSelector(getPendingAiSessionId);
  const activeSessionId = useSelector(getActiveAiSessionId);

  // Any pending generation wins; otherwise the session behind the results on screen.
  const sessionId =
    pendingSessionId || (orgCtx ? orgSessionId : activeSessionId);

  const sessionFeedbackState = useSelector(state =>
    getSessionFeedback(state, sessionId)
  );

  const [{ res: feedbackResponse, loading, error }, submitFeedback] = usePost({
    url: aiSearchFeedback,
  });

  const handleClose = useCallback(() => {
    dispatch(setShowAISearchFeedbackModal(false));
    setRating(null);
    setComment('');
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    // A rating is the only hard requirement. The AI-search session record is enrichment: a user can
    // open this modal with no search running and still submit how they feel.
    if (rating === null) {
      return;
    }

    submitFeedback({
      body: buildFeedbackPayload({
        sessionId,
        rating,
        comment,
        sessionFeedback: sessionFeedbackState,
        mainSearchView: pendingSearchView || view,
        orgCtx,
      }),
    });
  }, [
    sessionId,
    sessionFeedbackState,
    rating,
    comment,
    view,
    pendingSearchView,
    orgCtx,
    submitFeedback,
  ]);

  // Handle successful feedback submission
  useEffect(() => {
    if (feedbackResponse) {
      notification.success({
        message: 'Feedback Submitted',
        description: 'Thank you for your feedback!',
        duration: 3,
      });

      // Close the modal only; the session stays active so the chat/results on screen are
      // untouched and the modal can be re-opened against the same search.
      dispatch(setShowAISearchFeedbackModal(false));
      setRating(null);
      setComment('');
    }
  }, [feedbackResponse, dispatch]);

  // Handle error
  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Feedback Failed',
        description: 'Failed to submit feedback. Please try again.',
        duration: 5,
      });
    }
  }, [error]);

  if (!showModal) return null;

  return (
    <Modal
      data-testid='nlp-feedback-modal'
      open
      title={
        <TitleContainer>
          <span>{strings.feedbackModalTitle}</span>
          <BetaBadge>Beta</BetaBadge>
        </TitleContainer>
      }
      onCancel={handleClose}
      width={800}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button data-testid='nlp-feedback-cancel' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            data-testid='nlp-feedback-submit'
            type='primary'
            onClick={handleSubmit}
            loading={loading}
            disabled={rating === null || loading}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div>
        <p style={{ marginBottom: '8px' }}>
          {strings.feedbackModalRatingSelectorTitle}
        </p>
        <RatingContainer>
          {[1, 2, 3, 4, 5].map(value => (
            <RatingButton
              key={value}
              selected={rating === value}
              onClick={() => setRating(value)}
              data-testid={`nlp-feedback-rating-${value}`}
            >
              {value}
            </RatingButton>
          ))}
        </RatingContainer>
        <RatingLabels>
          <span>Not satisfied</span>
          <span>Extremely satisfied</span>
        </RatingLabels>
      </div>

      <div style={{ marginTop: '24px' }}>
        <p style={{ marginBottom: '8px' }}>{freeTextTitle}</p>
        <TextArea
          data-testid='nlp-feedback-comment'
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder={freeTextPlaceholder}
          rows={4}
          maxLength={1000}
        />
      </div>
    </Modal>
  );
}
